import { normalizeExerciseName, workoutExercises } from '@gymcloud/mixedscript'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { reached as fromTheServer, removed } from '~/components/use-work'
import { MacroPlate } from '~/components/macro-plate'
import { askForWeeklyCheckin } from '~/lib/weekly-checkin-ask'
import { useAuth } from '~/auth/auth-context'
import { AccountAvatar } from '~/components/account-avatar'
import { ActiveTemplateLine } from '~/components/active-template-line'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { DialogLink } from '~/components/dialog-link'
import {
  CalendarIcon,
  DeleteIcon,
  HistoryIcon,
  EditIcon,
  ExploreIcon,
  ListIcon,
  ScaleIcon,
  SettingsIcon,
  StopIcon,
  TargetIcon,
  UndoIcon,
} from '~/components/icons'
import { MetricsRow } from '~/components/metrics-row'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { useToast } from '~/components/toast-context'
import { useApi, useDataState } from '~/data/data-context'
import { useMetrics } from '~/data/metrics-store'
import {
  crossedTarget,
  currentGoal,
  currentGoalOn,
  progressToward,
  type Goal,
} from '~/data/nutrition'
import {
  dropGoal,
  dropOneGoal,
  keepGoal,
  reloadNutrition,
  useNutrition,
} from '~/data/nutrition-store'
import { todayKey, weighInEntry } from '~/data/weighins'
import { useWeighIns } from '~/data/weighins-store'
import { dayStart, formatDayShort } from '~/lib/dates'
import { withoutDialog } from '~/lib/dialogs'
import type { DayValue } from '~/lib/day-value'
import { aimSaid } from '~/lib/goal-aim'
import { goalsGoneBy } from '~/lib/goals-gone-by'
import { trendOf } from '~/lib/metric-series'
import { useActiveTemplate } from '~/lib/use-active-template'
import { formatWeight } from '~/lib/units'
import { weekdayNameOf, weekdayOf } from '~/lib/weekdays'

export const Route = createFileRoute('/you')({
  component: You,
})

/** A stretch of days as it is written on a card: "1 day", "12 days", "3 weeks". */
function formatStretch(days: number): string {
  if (days < 14) {
    return `${String(days)} ${days === 1 ? 'day' : 'days'}`
  }
  const weeks = Math.round(days / 7)
  return `${String(weeks)} weeks`
}

/** The stretch a goal runs over, worked out. */
interface Way {
  share: number
  fromKg: number
  targetKg: number
}

/**
 * How far along the goal is, from the weight it started against to the one it is aimed at —
 * the phone's own reckoning. Null where there is nothing to be a share of: no target weight,
 * or no weigh-in at all.
 */
function wayThrough(goal: Goal, points: readonly DayValue[]): Way | null {
  const target = goal.targetWeightKg
  if (target === null) {
    return null
  }
  const began = points.find((point) => point.day >= goal.effectiveFrom) ?? points[points.length - 1]
  const now = points[points.length - 1]
  if (began === undefined || now === undefined) {
    return null
  }
  const whole = target - began.value
  const share =
    Math.abs(whole) < 1e-6 ? 1 : Math.min(1, Math.max(0, (now.value - began.value) / whole))
  return { share, fromKg: began.value, targetKg: target }
}

/**
 * The dial's box, and its strokes — drawn the way the phone draws them. It is the page's
 * lead, so it is drawn at the size of one: beside a card as wide as the page, a dial the size
 * of a thumbnail read as a detail of the header rather than as the face the page is about.
 */
const DIAL = 216
const FACE = 156
const STROKE = 4
/** The ticks the ring is made of, and how far round they run — the phone's own dial. */
const TICKS = 44
const SWEEP = 280
const START = 130
/** How far in a lit tick reaches from the rim; a quiet one reaches a little over half of it. */
const TICK = 16
const QUIET_TICK = TICK * 0.55
/** How long the whole ring takes to light, and so how far apart two neighbouring ticks are. */
const FILL_MS = 700
/**
 * How far past the ring the two weights stand, and how far they ride up into the gap the ring
 * leaves at its foot. The phone's own two measures, scaled to the dial web draws.
 */
const END_ROOM = Math.round((12 / 176) * DIAL)
const END_LIFT = Math.round((18 / 176) * DIAL)

/**
 * The face inside the dial. With a goal the ring lights as far as the body has come and the two
 * weights stand at its foot; without one it is a quiet ring, so the face stands in the same
 * place either way and setting a goal lights it rather than moving it. Crossed once, the dial
 * stands full for good, and the trend's own share is drawn over it in the trend's colour.
 *
 * The ring is a run of upright ticks rather than a line, the way the phone draws it: a lit tick
 * reaches further in than a quiet one and wears the scale's colour, and the trend's share is
 * the same ticks in the trend's colour over the top. They light in turn from the ring's near
 * foot round to its far one, each a little after the one before, so the fill reads as a sweep
 * rather than a jump.
 */
function GoalDial({
  share,
  overShare,
  fromSaid,
  targetSaid,
}: {
  share: number | null
  overShare: number | null
  fromSaid: string | null
  targetSaid: string | null
}) {
  const outer = DIAL / 2 - STROKE / 2
  const middle = DIAL / 2
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        role="img"
        aria-label={
          share === null
            ? 'No goal to be a share of'
            : `${String(Math.round(share * 100))}% of the way from ${fromSaid ?? ''} to ${targetSaid ?? ''}`
        }
        className="relative grid place-items-center"
        style={{ width: DIAL, height: DIAL }}
      >
        <svg
          aria-hidden
          viewBox={`0 0 ${String(DIAL)} ${String(DIAL)}`}
          className="absolute inset-0"
        >
          {Array.from({ length: TICKS }, (_, index) => {
            const along = (index + 0.5) / TICKS
            const angle = ((START + along * SWEEP) * Math.PI) / 180
            const dx = Math.cos(angle)
            const dy = Math.sin(angle)
            const on = share !== null && along < share
            const trending = on && overShare !== null && along < overShare
            const reach = on ? TICK : QUIET_TICK
            return (
              <line
                key={index}
                {...(trending ? { 'data-trend-share': true } : on ? { 'data-share': true } : {})}
                x1={middle + dx * outer}
                y1={middle + dy * outer}
                x2={middle + dx * (outer - TICK)}
                y2={middle + dy * (outer - TICK)}
                strokeWidth={STROKE}
                strokeLinecap="round"
                /*
                 * Every tick is drawn its full length and shown from the rim inward as far as
                 * it reaches, because that is a dash and a dash can be animated where a line's
                 * own ends cannot.
                 */
                strokeDasharray={`${String(reach)} ${String(TICK)}`}
                stroke={
                  trending
                    ? 'var(--color-metric-trend)'
                    : on
                      ? 'var(--color-metric-scale)'
                      : 'color-mix(in srgb, currentColor 12%, transparent)'
                }
                className="text-ink transition-[stroke,stroke-dasharray] duration-200"
                style={{ transitionDelay: `${String(Math.round((along * FILL_MS) / 2))}ms` }}
              />
            )
          })}
        </svg>
        <AccountAvatar size={FACE} className="relative" />
      </div>
      {/*
        The two ends, under the two ends. The ring opens at its foot and the words stand in the
        gap it leaves — one against each end of it — so the dial is read as a stretch travelled
        from one weight to another rather than as a percentage with a caption under it.
      */}
      {fromSaid !== null && targetSaid !== null && (
        <p
          className="flex items-center justify-between text-sm"
          style={{ width: DIAL + END_ROOM * 2, marginTop: -END_LIFT }}
        >
          <span className="text-ink-muted">{fromSaid}</span>
          <span className="font-medium text-ink">{targetSaid}</span>
        </p>
      )}
    </div>
  )
}

/**
 * You — the app spec's third destination, named after whoever is signed in: not what you did
 * or what you ate, but how the app is set up for you, laid out as the phone lays it. The face
 * inside the goal's dial with the name and the aim under it; then one card holding this
 * week's two answers, the plan the goal decides and the ways off it; the template being run;
 * and last the metrics — what has been recorded about the days.
 *
 * The page waits as a whole until the goals have settled, and says nothing about the account,
 * the goal or the weigh-in before then.
 */
function You() {
  const { session, units } = useAuth()
  const api = useApi()
  const toast = useToast()
  const data = useNutrition()
  const weighIns = useWeighIns()
  const metrics = useMetrics()
  const state = useDataState()
  const { active, looked } = useActiveTemplate()
  /** Which of the three that cannot be taken back is being asked about, or null. */
  const [asking, setAsking] = useState<'end' | 'remove' | 'reopen' | null>(null)
  const today = todayKey()
  const name = session?.user.name ?? ''
  const first = name.split(' ')[0] ?? name

  if (data === null) {
    return (
      <SkeletonBlock label="Loading your goals" className="mx-auto max-w-3xl space-y-6">
        <Skeleton className="mx-auto size-32 rounded-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </SkeletonBlock>
    )
  }

  // The goal in force with what it is asking of today: its run of targets resolved down.
  const goal = currentGoalOn(data.goals, today)
  const weighed = [...(weighIns ?? [])]
    .filter((weighIn) => weighIn.weightKg !== null)
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((weighIn) => ({ day: weighIn.day, value: weighIn.weightKg ?? 0 }))
  const trend = trendOf(weighed)
  /* Whether the weigh-ins have arrived at all: nothing about the way through is said before. */
  const arrived = weighIns !== null
  const progress =
    goal?.targetWeightKg != null ? progressToward(trend, goal.targetWeightKg, today) : null
  const reached =
    goal !== null &&
    trend.some((point) => point.day >= goal.effectiveFrom && crossedTarget(goal, point.value))
  /*
   * Whether any weigh-in over the goal has crossed the target — the dial's sticky memory. The
   * scale wanders, and a full dial that emptied again because of one heavy morning would be
   * the goal taking back its word.
   */
  const scaleCrossed =
    goal !== null &&
    weighed.some((point) => point.day >= goal.effectiveFrom && crossedTarget(goal, point.value))
  /* The goals gone by, newest first, and the one reopening would bring back. */
  const gone = goalsGoneBy(data.goals, trend, today)
  const newestGone = gone[0] ?? null
  const way = goal === null ? null : wayThrough(goal, weighed)
  const trendWay = goal === null ? null : wayThrough(goal, trend)
  const sinceDays =
    goal === null
      ? 0
      : Math.round((dayStart(today) - dayStart(goal.effectiveFrom)) / 86_400_000) + 1
  const expectedIn =
    progress?.expectedDay == null
      ? null
      : Math.round((dayStart(progress.expectedDay) - dayStart(today)) / 86_400_000)
  const todays = weighIns?.find((weighIn) => weighIn.day === today)
  const weighedToday = todays?.weightKg != null
  const library = state.templates
  const running = state.enrollments.find((enrollment) => enrollment.endedAt === null) ?? null
  const editable = running === null ? null : library.find((each) => each.id === running.templateId)
  /*
   * What the run's program amounts to. Exercises are counted by name rather than by mention,
   * folded the way every exercise name is folded, so a template pushing bench press on three
   * days is a program of one exercise there and not three.
   */
  const amounts =
    active?.node == null
      ? null
      : {
          workouts: active.node.workouts.length,
          exercises: new Set(
            active.node.workouts.flatMap((workout) =>
              workoutExercises(workout)
                .map((exercise) => normalizeExerciseName(exercise.name))
                .filter((name) => name.length > 0),
            ),
          ).size,
          began: active.enrollment.startedAt,
        }
  /* How long until the weekly check-in: the days to its weekday, counted from today. */
  const checkinIn =
    goal?.mode !== 'guided'
      ? null
      : (goal.weeklyCheckinWeekday - weekdayOf(dayStart(today)) + 7) % 7

  /*
   * Ending and removing are two different things and the card offers both — see the nutrition
   * spec, The goal itself. Ending stops the goal on today and keeps it as one gone by, and
   * every day already logged keeps the targets that stood on it; removing destroys the whole
   * history, so no day is read against a target any more. Each is asked from its confirmation,
   * which says in itself where the server was not reached and stays to be tried again — see
   * `reached` in `use-work.ts`.
   */
  const endGoal = async () => {
    keepGoal(await fromTheServer(() => api.endGoal(today)))
    reloadNutrition()
    toast('The goal has ended.')
  }

  const removeGoal = async () => {
    await fromTheServer(() => removed(() => api.deleteGoal()))
    dropGoal()
    reloadNutrition()
    toast('The goal has been removed.')
  }

  /*
   * The newest goal gone by, in force again. What stood over it — the goal that replaced it,
   * or the ending of it — never happened: a goal that was in force is destroyed outright
   * rather than kept as one gone by, which is why this asks first.
   */
  const reopenGoal = async () => {
    const undone = currentGoal(data.goals)
    keepGoal(await fromTheServer(() => api.reopenGoal()))
    if (undone !== null) {
      dropOneGoal(undone.id)
    }
    reloadNutrition()
    toast('The goal is back in force.')
  }

  const card = 'rounded-2xl bg-surface-raised ring-1 ring-white/10'
  /*
   * One reading of how the goal is going, written under the word for what it is. A row of them
   * leans out from the middle — the first against the left edge, the last against the right —
   * so three readings read as spread across the card rather than as three things huddled on
   * the left of it, the way the phone writes them.
   */
  const figure = (value: string, label: string, lean: 'start' | 'center' | 'end' = 'start') => (
    <div
      className={`flex min-w-0 flex-col ${
        lean === 'start' ? 'items-start' : lean === 'center' ? 'items-center' : 'items-end'
      }`}
    >
      <p className="max-w-full truncate text-lg font-medium">{value}</p>
      <p className="max-w-full truncate text-xs text-ink-muted">{label}</p>
    </div>
  )
  const row =
    'flex w-full items-center gap-3 px-5 py-3 text-left text-sm transition-colors hover:bg-hover'

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="sr-only">{first}</h1>

      {/*
        Settings, at the far corner of the top of the page — the app spec, You. It is the page
        about the account, so it is where everything about the account is reached from, and
        settings carries feedback and the way out under its own sections.
      */}
      <div className="flex justify-end">
        <DialogLink dialog="settings" label="Settings" className="icon-btn">
          <SettingsIcon aria-hidden className="size-6" />
        </DialogLink>
      </div>

      {/* The face inside the dial, the name, and one line: what you aim at, and what you run. */}
      <div className="flex flex-col items-center gap-1 pt-2 text-center">
        <GoalDial
          share={way === null ? null : !arrived ? 0 : scaleCrossed ? 1 : way.share}
          overShare={scaleCrossed && trendWay !== null ? (arrived ? trendWay.share : 0) : null}
          fromSaid={way === null ? null : formatWeight(way.fromKg, units)}
          targetSaid={way === null ? null : formatWeight(way.targetKg, units)}
        />
        <p className="mt-2 truncate text-xl font-semibold">{name}</p>
        {goal === null ? (
          <>
            <p className="text-sm">No body goal yet</p>
            <p className="text-xs text-ink-muted">
              A body goal sets what you are aiming at and what each day should come to.
            </p>
          </>
        ) : (
          looked && (
            <p className="text-sm">
              {[aimSaid(goal, units), active?.name ?? null]
                .filter((part) => part !== null)
                .join(' · ')}
            </p>
          )
        )}
      </div>

      {/* This week's two answers, the plan the goal decides, and the ways off it. */}
      <section
        aria-label={goal === null ? 'No body goal' : 'Active body goal'}
        className={`${card} overflow-hidden`}
      >
        {/* Titled the way the template's card is, saying what it holds before it is read. */}
        <h2 className="px-5 pt-5 text-xs font-medium tracking-wider text-ink-muted uppercase">
          Active body goal
        </h2>
        <div className="grid grid-cols-1 divide-white/10 sm:grid-cols-2 sm:divide-x">
          {/* Today's weigh-in, saying what the day already holds. */}
          <div aria-label="Daily weigh-in" role="region">
            {weighIns === null ? (
              <div role="status" aria-label="Loading today's weigh-in" className="px-5 py-4">
                <Skeleton className="h-9 w-40 rounded-xl" />
              </div>
            ) : (
              <DialogLink
                dialog="weighin-weight"
                className={`${row} flex-col items-start gap-1.5 py-4`}
              >
                <span
                  className={`grid size-9 place-items-center rounded-full ${
                    weighedToday ? 'bg-accent/15 text-accent' : 'bg-white/5 text-ink-muted'
                  }`}
                >
                  <ScaleIcon aria-hidden className="size-5" />
                </span>
                <span className="block font-medium">Daily weigh-in</span>
                <span className="block text-ink-muted">
                  {todays?.weightKg == null
                    ? 'Not yet today'
                    : weighInEntry(formatWeight(todays.weightKg, units), todays.photos.length)}
                </span>
                <span className="block text-xs text-accent">
                  {weighedToday ? 'Open' : 'Weigh in'}
                </span>
              </DialogLink>
            )}
          </div>
          {/*
            The weekly check-in. A goal set by hand keeps the tile and loses the press — the
            nutrition spec, The weekly check-in: taking it away would leave the week's row
            looking like a row with one thing in it, and somebody who set their targets by hand
            would never learn what they gave up by doing so.
          */}
          {goal !== null &&
            (checkinIn === null ? (
              <div
                aria-label="Weekly check-in, coached goals only"
                role="region"
                className="flex flex-col items-start gap-1.5 px-5 py-4 text-sm opacity-45"
              >
                <span className="grid size-9 place-items-center rounded-full bg-white/5 text-ink-muted">
                  <CalendarIcon aria-hidden className="size-5" />
                </span>
                <span className="block font-medium">Weekly check-in</span>
                <span className="block text-ink-muted">Set by hand</span>
                <span className="block text-xs text-ink-muted">Coached goals only</span>
              </div>
            ) : (
              <button
                type="button"
                aria-label={
                  checkinIn === 0
                    ? 'Weekly check-in due now'
                    : `Weekly check-in on ${weekdayNameOf(goal.weeklyCheckinWeekday)}, in ${String(checkinIn)} ${checkinIn === 1 ? 'day' : 'days'}`
                }
                className={`${row} flex-col items-start gap-1.5 py-4 text-left`}
                onClick={askForWeeklyCheckin}
              >
                <span
                  className={`grid size-9 place-items-center rounded-full ${
                    checkinIn === 0 ? 'bg-accent/15 text-accent' : 'bg-white/5 text-ink-muted'
                  }`}
                >
                  <CalendarIcon aria-hidden className="size-5" />
                </span>
                <span className="block font-medium">Weekly check-in</span>
                <span className="block text-ink-muted">
                  {checkinIn === 0
                    ? 'Due now'
                    : `In ${String(checkinIn)} ${checkinIn === 1 ? 'day' : 'days'} · ${weekdayNameOf(goal.weeklyCheckinWeekday)}`}
                </span>
                <span className="block text-xs text-accent">
                  {checkinIn === 0 ? 'Check in now' : 'Check in early'}
                </span>
              </button>
            ))}
        </div>

        <div className="border-t border-white/10 px-5 py-4">
          {goal === null ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ink-muted">No body goal yet</p>
              <Link
                to="/you/goal"
                search={withoutDialog}
                className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-ink"
              >
                Set a body goal
              </Link>
            </div>
          ) : (
            <div className="space-y-5">
              {/* How long it has run, how far there is left, and how long that should take. */}
              <div className="grid grid-cols-3 gap-4">
                {figure(
                  formatStretch(sinceDays),
                  `Since ${formatDayShort(dayStart(goal.effectiveFrom))}`,
                  'start',
                )}
                {reached
                  ? figure('Reached', 'Goal', 'center')
                  : progress === null
                    ? figure(
                        goal.targetWeightKg === null
                          ? '—'
                          : formatWeight(goal.targetWeightKg, units),
                        'Target',
                        'center',
                      )
                    : figure(
                        formatWeight(Math.abs(progress.distanceKg), units),
                        progress.distanceKg < 0 ? 'To lose' : 'To gain',
                        'center',
                      )}
                {reached || expectedIn === null
                  ? figure(`${goal.calories.toLocaleString()} kcal`, 'A day', 'end')
                  : figure(
                      expectedIn <= 0 ? 'Today' : formatStretch(expectedIn),
                      expectedIn <= 0
                        ? 'Expected'
                        : `About ${formatDayShort(dayStart(progress?.expectedDay ?? today))}`,
                      'end',
                    )}
              </div>
              {/* What the goal asks of a day, and what those calories are made of. */}
              <MacroPlate
                calories={goal.calories}
                protein={goal.protein}
                carbs={goal.carbs}
                fat={goal.fat}
                arrived={arrived}
              />
            </div>
          )}
        </div>

        {/* The ways off the card, in one make: the goal's own, and the one that ends it. */}
        {(goal !== null || gone.length > 0) && (
          <div className="divide-y divide-white/5 border-t border-white/10">
            {goal !== null && (
              <Link to="/you/goal" search={withoutDialog} className={row}>
                <TargetIcon aria-hidden className="size-5 text-ink-muted" />
                {reached ? 'Start a new goal' : 'Change the goal'}
              </Link>
            )}
            {/*
              The goals gone by, where there are any: how many, and the way to the page they
              are read on. With none it says nothing and leads nowhere — a row reading "0 goals
              gone by" is a row about an absence.
            */}
            {gone.length > 0 && (
              <Link to="/you/goals-gone-by" search={withoutDialog} className={row}>
                <HistoryIcon aria-hidden className="size-5 text-ink-muted" />
                {gone.length === 1 ? '1 goal gone by' : `${String(gone.length)} goals gone by`}
              </Link>
            )}
            {/*
              Reopening the newest gone by, offered only where one has. It stands here, where
              the goal in force stands, because that is the thing it replaces.
            */}
            {newestGone !== null && (
              <button
                type="button"
                className={row}
                onClick={() => {
                  setAsking('reopen')
                }}
              >
                <UndoIcon aria-hidden className="size-5 text-ink-muted" />
                <span className="min-w-0">
                  <span className="block">Reopen {aimSaid(newestGone.goal, units)}</span>
                  <span className="block text-xs text-ink-muted">
                    The newest goal gone by, in force again.
                  </span>
                </span>
              </button>
            )}
            {/*
              Ending stops the goal and keeps it; removing destroys the whole history. Both ask
              first, and only the one that cannot be undone reads as destructive.
            */}
            {goal !== null && (
              <button
                type="button"
                className={row}
                onClick={() => {
                  setAsking('end')
                }}
              >
                <StopIcon aria-hidden className="size-5 text-ink-muted" />
                <span className="min-w-0">
                  <span className="block">End the goal</span>
                  <span className="block text-xs text-ink-muted">
                    It joins the goals gone by, and every day already logged keeps its targets.
                  </span>
                </span>
              </button>
            )}
            {goal !== null && (
              <button
                type="button"
                className={`${row} text-danger`}
                onClick={() => {
                  setAsking('remove')
                }}
              >
                <DeleteIcon aria-hidden className="size-5" />
                <span className="min-w-0">
                  <span className="block">Remove the goal</span>
                  <span className="block text-xs text-ink-muted">
                    With it go the targets that stood on every day.
                  </span>
                </span>
              </button>
            )}
          </div>
        )}
      </section>

      {/* The template being run, and the ways to the templates and to Explore. */}
      <section aria-label="Active template" className={`${card} p-5`}>
        <h2 className="mb-2 text-xs font-medium tracking-wider text-ink-muted uppercase">
          Active template
        </h2>
        <ActiveTemplateLine active={active} library={library} looked={looked} />
        {/*
          What the program amounts to, beside what it offers next: the size of the thing you
          have signed up for, and how long you have been at it. Nothing at all where the script
          has not been read — a run whose version has not landed, or one that will not parse,
          has no workouts to count, and counting nought would read as a program with none.
        */}
        {amounts !== null && (
          <dl className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm text-ink-muted">
            <div className="flex items-baseline gap-1.5">
              <dt className="sr-only">Workouts</dt>
              <dd>
                {amounts.workouts === 1 ? '1 workout' : `${String(amounts.workouts)} workouts`}
              </dd>
            </div>
            <div className="flex items-baseline gap-1.5">
              <dt className="sr-only">Exercises</dt>
              <dd>
                {amounts.exercises === 1 ? '1 exercise' : `${String(amounts.exercises)} exercises`}
              </dd>
            </div>
            <div className="flex items-baseline gap-1.5">
              <dt className="sr-only">Began</dt>
              <dd>Began {formatDayShort(amounts.began)}</dd>
            </div>
          </dl>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <DialogLink
            dialog="templates"
            className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm ring-1 ring-white/15 transition-colors hover:bg-hover"
          >
            <ListIcon aria-hidden className="size-5 text-ink-muted" />
            Templates
          </DialogLink>
          <DialogLink
            dialog="explore"
            className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm ring-1 ring-white/15 transition-colors hover:bg-hover"
          >
            <ExploreIcon aria-hidden className="size-5 text-ink-muted" />
            {looked && active === null ? 'Find a template' : 'Explore'}
          </DialogLink>
          {editable !== undefined && editable !== null && (
            <Link
              to="/templates/$templateId/edit"
              params={{ templateId: editable.id }}
              search={withoutDialog}
              className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm ring-1 ring-white/15 transition-colors hover:bg-hover"
            >
              <EditIcon aria-hidden className="size-5 text-ink-muted" />
              Edit active template
            </Link>
          )}
        </div>
      </section>

      {/* The metrics, last: what has been recorded about the days, a card apiece. */}
      {weighIns === null || metrics === null ? (
        <SkeletonBlock label="Loading the metrics">
          <Skeleton className="h-56 w-full" />
        </SkeletonBlock>
      ) : (
        <MetricsRow weighIns={weighIns} metrics={metrics} nutrition={data} layout="grid" />
      )}

      {asking === 'end' && goal !== null && (
        <ConfirmDialog
          title="End the goal?"
          body="From today no day carries a target. The days already logged keep the targets that stood on them, and the goal joins the goals gone by."
          confirm="End it"
          onConfirm={async () => {
            await endGoal()
            setAsking(null)
          }}
          onDismiss={() => {
            setAsking(null)
          }}
        />
      )}
      {asking === 'remove' && goal !== null && (
        <ConfirmDialog
          title="Remove the goal?"
          body="With it go the targets that stood on every day, and every day names its total and no target."
          confirm="Remove"
          onConfirm={async () => {
            await removeGoal()
            setAsking(null)
          }}
          onDismiss={() => {
            setAsking(null)
          }}
        />
      )}
      {asking === 'reopen' && (
        <ConfirmDialog
          title={`Reopen ${newestGone === null ? 'the last goal' : aimSaid(newestGone.goal, units)}?`}
          body={
            goal === null
              ? 'It is the goal in force again, from today.'
              : 'It is the goal in force again. The goal you are on now is destroyed rather than kept as one gone by.'
          }
          confirm="Reopen"
          onConfirm={async () => {
            await reopenGoal()
            setAsking(null)
          }}
          onDismiss={() => {
            setAsking(null)
          }}
        />
      )}
    </div>
  )
}
